import React, { type ReactNode } from 'react';
import { Button } from 'antd';
import { Colors } from '@common/tokens';
import { FilterButton } from './_filter-button';
import {
  DateRangeFilterButton,
  type DateRangeFilterValue,
} from './_date-range-filter-button';
import { CurrencyRangeFilterButton } from './_currency-range-filter-button';
import { NumericRangeFilterButton } from './_numeric-range-filter-button';
import { Icon } from '@common/components';
import type {
  FilterFormat,
  TableFilterControlsColumnType,
} from './_filtered-table-types';

function getFilterLabelFromColumns(
  key: string,
  value: string | number | boolean,
  columns: TableFilterControlsColumnType[]
): ReactNode {
  const filterMap = columns.find((column) => column.key === key)?.filters || [];

  const label = filterMap.find((filter) => {
    return filter.value === value || filter.id === value;
  });

  return label ? label.labelText || label.text || label.name : value;
}

function getFormatterFromColumns(
  key: string,
  columns: TableFilterControlsColumnType[]
): FilterFormat | undefined {
  return columns.find((column) => column.key === key)?.filterFormat;
}

type TableFilterControlsProps = {
  filters: Record<string, unknown>;
  clearFilter: (
    column: string,
    value?: string | number | boolean | null
  ) => void;
  clearAllFilters: () => void;
  columns: TableFilterControlsColumnType[];
};

export const TableFilterControls = ({
  filters,
  clearFilter,
  clearAllFilters,
  columns,
}: TableFilterControlsProps) => {
  const filtersEls = Object.keys(filters)
    .filter((key) => filters[key] !== null)
    .map((key) => {
      const fmtColumns = getFormatterFromColumns(key, columns);

      const values = filters[key];
      if (!Array.isArray(values) || !values.length) {
        return [];
      }

      if (fmtColumns === 'dateRange') {
        return (
          <DateRangeFilterButton
            key={key}
            keyName={key}
            startDateArray={
              values as [DateRangeFilterValue, DateRangeFilterValue]
            }
            clearFilter={clearFilter}
          />
        );
      }

      if (fmtColumns === 'currencyRange') {
        return (
          <CurrencyRangeFilterButton
            key={key}
            keyName={key}
            rangeArray={values as [string, string]}
            clearFilter={clearFilter}
          />
        );
      }

      if (
        fmtColumns === 'hoursRange' ||
        fmtColumns === 'weeksRange' ||
        fmtColumns === 'percentRange'
      ) {
        return (
          <NumericRangeFilterButton
            key={key}
            keyName={key}
            rangeArray={values as [number, number]}
            clearFilter={clearFilter}
            unit={
              fmtColumns === 'percentRange' ? '%' : fmtColumns.split('Range')[0]
            }
          />
        );
      }

      return (values as unknown[]).map((value) => {
        const coerced = value as string | number | boolean;
        let labelName = getFilterLabelFromColumns(key, coerced, columns);

        if (getFormatterFromColumns(key, columns) === 'id') {
          labelName = String(coerced).toUpperCase();
        }

        return (
          <FilterButton
            key={`${key}-${String(coerced)}`}
            keyName={key}
            value={coerced}
            clearFilter={clearFilter}
            labelName={String(labelName)}
          />
        );
      });
    })
    .flat();

  return (
    <>
      <div className="table-filter-controls">
        {filtersEls}
        {filtersEls.length > 1 && (
          <Button onClick={clearAllFilters}>
            <Icon icon="xMark" kind="light" />
            Clear All
          </Button>
        )}
      </div>
      <style jsx>
        {`
          .table-filter-controls :global(.ant-btn) {
            text-transform: capitalize;
          }
          .table-filter-controls :global(button.ant-btn) {
            background-color: ${Colors.midnightLighter};
            color: white;
            margin-right: 5px;
            margin-bottom: 5px;
          }
          .table-filter-controls :global(button.ant-btn:hover) {
            border-color: ${Colors.midnightLighter};
          }
          .table-filter-controls :global(button.ant-btn:last-child) {
            margin-bottom: 12px;
          }
          .table-filter-controls :global(button.ant-btn .svg-inline--fa) {
            margin-right: 6px;
            padding-top: 3px;
          }
        `}
      </style>
    </>
  );
};
